import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, CheckCircle, Search, FileText,
  Globe, TrendingUp, Link, BarChart2, Zap, Star,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PageHero from "./PageHero";
import PortfolioFooter from "./PortfolioFooter";

const gradientText = "bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent";
const gradientBg   = "bg-gradient-to-r from-purple-700 to-pink-600";
const iconBg       = "bg-purple-50 border border-purple-100";
const iconColor    = "text-purple-500";
const checkColor   = "text-purple-500";
const statsBg      = "bg-purple-900";
const ctaBg        = "bg-purple-950";
const sectionAlt   = "bg-purple-50";

const services = [
  {
    icon: <Search className={`w-7 h-7 ${iconColor}`} />,
    title: "Technical SEO",
    desc: "We audit and fix the invisible foundations that hold your rankings back — site speed, crawlability, Core Web Vitals, schema markup, and indexation.",
    features: ["Full technical SEO audit", "Core Web Vitals optimisation", "Schema markup implementation", "XML sitemap & robots.txt setup"],
  },
  {
    icon: <FileText className={`w-7 h-7 ${iconColor}`} />,
    title: "Content Strategy & Creation",
    desc: "Keyword-rich, expert-written content that ranks, educates, and converts — from pillar pages to blog posts to case studies.",
    features: ["Keyword research & content mapping", "Long-form blog & pillar content", "Content calendar management", "Existing content optimisation"],
  },
  {
    icon: <Link className={`w-7 h-7 ${iconColor}`} />,
    title: "Link Building & Authority",
    desc: "White-hat outreach campaigns that earn high-authority backlinks from industry-relevant sites, boosting your domain rating and rankings.",
    features: ["Competitor backlink analysis", "Guest posting & digital PR", "Broken link reclamation", "Monthly link acquisition reports"],
  },
  {
    icon: <BarChart2 className={`w-7 h-7 ${iconColor}`} />,
    title: "SEO Analytics & Reporting",
    desc: "Monthly reports covering organic traffic, keyword rankings, CTR, and revenue attribution — with clear recommendations for next steps.",
    features: ["Rank tracking (100+ keywords)", "Organic traffic & CTR reports", "Google Search Console insights", "Quarterly strategy reviews"],
  },
];

const stats = [
  { value: "2.5×",  label: "Avg Organic Traffic Growth", icon: <TrendingUp className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "Top 3", label: "Avg Keyword Rankings",        icon: <Star className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "90d",   label: "Avg Time-to-Rankings",        icon: <Zap className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "60+",   label: "Brands Ranked",               icon: <Globe className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
];

const whyUs = [
  "White-hat tactics — no penalties, ever",
  "Dedicated SEO strategist per client",
  "Monthly content production included",
  "Transparent rank tracking every week",
  "Integrated with your dev & content teams",
  "E-E-A-T compliant content frameworks",
];

const process = [
  { step: "01", title: "SEO Audit",           desc: "Full technical, on-page, and backlink audit of your current site." },
  { step: "02", title: "Keyword Strategy",    desc: "Map high-value keywords to your core pages and content plan." },
  { step: "03", title: "On-Page Optimisation",desc: "Fix titles, meta tags, headings, internal links, and content gaps." },
  { step: "04", title: "Content Production",  desc: "Publish expert content that targets your priority keywords." },
  { step: "05", title: "Link Acquisition",    desc: "Build authority through strategic outreach and digital PR." },
  { step: "06", title: "Track & Iterate",     desc: "Monthly reporting with strategy refinements based on data." },
];

const SEOContent = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans">
      <Helmet>
        <title>SEO & Content Strategy | Winsil Evolution</title>
        <meta name="description" content="Rank higher on Google with Winsil Evolution's SEO and content strategy services — technical SEO, content creation, link building and transparent reporting." />
      </Helmet>

      <PortfolioNavbar />

      <PageHero
        label="Branding — SEO"
        labelClass={`font-black ${gradientText}`}
        title="SEO & Content Strategy"
        subtitle="Sustainable organic growth through technical excellence, expert content, and authoritative link building."
        image="/images/photo-1432888498266-38ffec3eaf0a.jpg"
        bgClass="bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"
        glowColor="#d946ef"
        titleClass="text-white"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>SEO in Practice</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Content That Ranks & Converts</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1542744173-8e7e53415bb0.jpg", alt: "Content writing and strategy", label: "Content Strategy" },
              { src: "/images/photo-1499750310107-5fef28a66643.jpg", alt: "SEO research and writing", label: "Keyword Research" },
              { src: "/images/photo-1519389950473-47ba0277781c.jpg", alt: "Analytics and rank tracking", label: "Rank Tracking" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1432888498266-38ffec3eaf0a.jpg", alt: "Link building outreach", label: "Link Building" },
              { src: "/images/photo-1504868584819-f8e8b4b6d7e3.jpg", alt: "SEO performance reporting", label: "SEO Reporting" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Services Grid ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>What We Offer</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">Our SEO Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div key={i} className="group bg-gray-50 border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className={`h-1 -mx-8 -mt-8 mb-7 rounded-t-2xl ${gradientBg}`} />
                <div className={`w-14 h-14 ${iconBg} rounded-xl flex items-center justify-center mb-5 group-hover:bg-purple-100 transition-colors duration-300`}>
                  {s.icon}
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{s.desc}</p>
                <ul className="space-y-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center space-x-2">
                      <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 ${checkColor}`} />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Process ── */}
      <div className={`py-20 px-4 sm:px-6 lg:px-8 ${sectionAlt}`}>
        <div className="max-w-7xl mx-auto">
          <p className={`text-xs font-black uppercase tracking-[0.25em] text-center mb-2 ${gradientText}`}>How We Work</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">Our SEO Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((p) => (
              <div key={p.step} className="relative bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group">
                <span className="absolute -top-2 -right-1 text-8xl font-black text-gray-50 group-hover:text-purple-50 transition-colors duration-300 select-none leading-none pointer-events-none">
                  {p.step}
                </span>
                <div className="relative z-10">
                  <p className={`text-[11px] font-black uppercase tracking-widest mb-3 ${gradientText}`}>Step {p.step}</p>
                  <h3 className="text-lg font-extrabold text-gray-900 mb-2">{p.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={`py-16 px-4 sm:px-6 lg:px-8 ${statsBg}`}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {stats.map((s) => (
            <div key={s.label}>
              {s.icon}
              <p className="text-4xl sm:text-5xl font-black">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/50 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Why Us ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className={`text-xs font-black uppercase tracking-[0.25em] mb-3 ${gradientText}`}>Why Winsil</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                SEO That{" "}
                <span className={gradientText}>Compounds Over Time</span>
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Unlike paid ads that stop the moment you stop spending, great SEO builds lasting
                organic equity. We focus on sustainable strategies that keep working month after month.
              </p>
              <ul className="space-y-3 mb-10">
                {whyUs.map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <CheckCircle className={`w-4 h-4 flex-shrink-0 ${checkColor}`} />
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/contact")}
                className={`inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg ${gradientBg} text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25`}
              >
                Get a Free SEO Audit
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="/images/photo-1504868584819-f8e8b4b6d7e3.jpg"
                alt="SEO analytics and content strategy"
                className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/60 via-transparent to-pink-500/10" />
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className={`text-2xl font-black ${gradientText}`}>2.5×</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Organic Growth</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className={`relative ${ctaBg} py-16 px-4 overflow-hidden text-white text-center`}>
        <div className={`absolute inset-0 opacity-20 ${gradientBg}`} />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 max-w-xl mx-auto">
          <p className={`text-[11px] font-black uppercase tracking-[0.25em] mb-3 ${gradientText}`}>Start Ranking</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Let's Get You to Page One</h2>
          <p className="text-white/55 mb-8">Free SEO audit delivered within 48 hours. No commitment required.</p>
          <button
            onClick={() => navigate("/contact")}
            className={`inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-full ${gradientBg} text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30`}
          >
            Claim Your Free Audit
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default SEOContent;
